import { ExportItem, ExportVisibility } from '@aibees/elephant/hooks';
import { SearchType } from '@aibees/elephant/types';
import {
  dataRequestParser,
  dataRequestValueFormatter,
} from '../components/filter-tags/utils';
import {
  ClassificationGroups,
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';
import { exportDataRequestApiMapper } from '../utils';

export default function exportsRequestFilterConfig(
  searchType: SearchType
): SearchFilterBase {
  const companiesExportsRequest: SearchFilter = {
    name: 'Company',
    type: SearchFilterType.NONE,
    index: 2,
    key: 'companies_exports_request',
    field: 'companies_exports_request',
    metadata: {
      tabs: {
        icon: 'business',
        iconColor: 'primary',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Company Export Requests',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'companies_exports_request_include',
        key: 'companies_exports_request_include',
        icon: 'business',
        metadata: {
          delimiter: '::',
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Select Company Exports',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: ExportItem[]) =>
            exportDataRequestApiMapper(data),
          valueFormatter: dataRequestValueFormatter,
          tags: { labelFormatter: dataRequestParser },
        },
        searchKey: 'name',
        config: { labelKey: 'name', valueKey: 'id' },
        options: [
          {
            type: 'URL',
            value: `elephant-management/um/workspaces/:wid/exports?page=0&size=20&grouping=${ClassificationGroups.COMPANY}&visibility=${ExportVisibility.PRIVATE}`,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: ['companies_exports_request_query'],
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        key: 'companies_exports_request_exclude',
        field: 'companies_exports_request_exclude',
        icon: 'business',
        metadata: {
          delimiter: '::',
          color: 'error',
          icon: 'block',
          placeholder: 'Select Company Exports',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: ExportItem[]) =>
            exportDataRequestApiMapper(data),
          valueFormatter: dataRequestValueFormatter,
          tags: { labelFormatter: dataRequestParser },
        },
        searchKey: 'name',
        config: { labelKey: 'name', valueKey: 'id' },
        options: [
          {
            type: 'URL',
            value: `elephant-management/um/workspaces/:wid/exports?page=0&size=20&grouping=${ClassificationGroups.COMPANY}&visibility=${ExportVisibility.PRIVATE}`,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: ['companies_exports_request_query'],
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 2,
        layer: 4,
        field: 'companies_exports_request_all_of',
        key: 'companies_exports_request_all_of',
        icon: 'business',
        metadata: {
          delimiter: '::',
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Select Company Exports',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: ExportItem[]) =>
            exportDataRequestApiMapper(data),
          valueFormatter: dataRequestValueFormatter,
          tags: { labelFormatter: dataRequestParser },
        },
        searchKey: 'name',
        config: { labelKey: 'name', valueKey: 'id' },
        options: [
          {
            type: 'URL',
            value: `elephant-management/um/workspaces/:wid/exports?page=0&size=20&grouping=${ClassificationGroups.COMPANY}&visibility=${ExportVisibility.PRIVATE}`,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: ['companies_exports_request_query'],
          },
        ],
      },
    ],
  };
  const peopleExportsRequest: SearchFilter = {
    name: 'People',
    type: SearchFilterType.NONE,
    index: 3,
    key: 'people_exports_request',
    field: 'people_exports_request',
    metadata: {
      tabs: {
        icon: 'people',
        iconColor: 'success',
      },
    },
    comingSoon: searchType === SearchType.Company, // TODO: remove this once the people export for company search is ready on the backend
    disabled: searchType === SearchType.Company, // TODO: remove this once the people export for company search is ready on the backend
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'People Export Requests',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'people_exports_request_include',
        key: 'people_exports_request_include',
        icon: 'people',
        metadata: {
          delimiter: '::',
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Select People Exports',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: ExportItem[]) =>
            exportDataRequestApiMapper(data),
          valueFormatter: dataRequestValueFormatter,
          tags: { labelFormatter: dataRequestParser },
        },
        searchKey: 'name',
        config: { labelKey: 'name', valueKey: 'id' },
        options: [
          {
            type: 'URL',
            value: `elephant-management/um/workspaces/:wid/exports?page=0&size=20&grouping=${ClassificationGroups.PERSON}&visibility=${ExportVisibility.PRIVATE}`,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: ['people_exports_request_query'],
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        key: 'people_exports_request_exclude',
        field: 'people_exports_request_exclude',
        icon: 'people',
        metadata: {
          delimiter: '::',
          color: 'error',
          icon: 'block',
          placeholder: 'Select People Exports',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: ExportItem[]) =>
            exportDataRequestApiMapper(data),
          valueFormatter: dataRequestValueFormatter,
          tags: { labelFormatter: dataRequestParser },
        },
        searchKey: 'name',
        config: { labelKey: 'name', valueKey: 'id' },
        options: [
          {
            type: 'URL',
            value: `elephant-management/um/workspaces/:wid/exports?page=0&size=20&grouping=${ClassificationGroups.PERSON}&visibility=${ExportVisibility.PRIVATE}`,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: ['people_exports_request_query'],
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 2,
        layer: 4,
        field: 'people_exports_request_all_of',
        key: 'people_exports_request_all_of',
        icon: 'people',
        metadata: {
          delimiter: '::',
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Select People Exports',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: ExportItem[]) =>
            exportDataRequestApiMapper(data),
          valueFormatter: dataRequestValueFormatter,
          tags: { labelFormatter: dataRequestParser },
        },
        searchKey: 'name',
        config: { labelKey: 'name', valueKey: 'id' },
        options: [
          {
            type: 'URL',
            value: `elephant-management/um/workspaces/:wid/exports?page=0&size=20&grouping=${ClassificationGroups.PERSON}&visibility=${ExportVisibility.PRIVATE}`,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: ['people_exports_request_query'],
          },
        ],
      },
    ],
  };

  return {
    name: `Export Requests`,
    icon: searchType === SearchType.Company ? 'domain-add' : 'user-add2',
    index: 11,
    type: SearchFilterType.ROOT,
    pinned: true,
    category: [FilterCategory.RECORD_MANAGEMENT],
    layer: 3,
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        metadata: {
          allowParallelItems: true,
          tabs: {
            defaultOpenIndex: searchType === SearchType.Company ? 0 : 1,
          },
        },
        config: { labelKey: 'label', valueKey: 'value' },
        items: [
          ...[
            {
              name: 'Applied Filters',
              type: SearchFilterType.APPLIED_FILTERS,
              index: 2,
              icon: undefined,
              key: 'exports_requests_applied_filters',
              field: 'exports_requests_applied_filters',
              metadata: {},
            },
          ],
          ...[companiesExportsRequest, peopleExportsRequest],
        ],
      },
    ],
  };
}
